import { useEcgCanvas } from '@/mixins/ecg-canvas.js'
import getSignal from '@/modules/ecg-viewer-real-time.js'

const RENDER_CONSTANTS = {
  TIME_STEP: 0.004, // Шаг времени для анимации
  BASE_OFFSET: 40, // Базовый отступ для первого графика
  LEAD_OFFSET: 6, // Отступ между графиками (умножается на scaleY)
  MIN_BUFFER_PACKETS: 5, // Минимальное количество пакетов для принудительного рендера
  RENDER_SPEED_MULTIPLIER: 4 // Множитель скорости рендеринга
}

export const useEcgOnlineCanvas = {
  mixins: [useEcgCanvas],
  methods: {
    initializeBuffers(arraySize) {
      this.source = this.allocArray(arraySize)
      this.dest = this.allocArray(arraySize)
      this.indexFilter = this.initFilters(
        this.nbOfSamples * this.leadscount,
        this.leadscount,
        this.nbOfSamples
      )
    },
    manageWasmBuffers(action = 'init') {
      try {
        if (!this.moduleInstance) throw new Error('WASM module not initialized')

        if (action === 'init') {
          this.freeWasmResources()
          this.initializeBuffers(this.nbOfSamples * this.leadscount)
          return true
        }

        if (action === 'free') {
          this.freeWasmResources()
          return true
        }
      } catch (error) {
        console.error('WASM buffer management error:', error)
        this.handleWasmFailure()
        return false
      }
    },
    // Освобождаем буферы
    freeWasmResources() {
      if (this.source) {
        this.freeBuffer(this.source)
        this.source = null
      }
      if (this.dest) {
        this.freeBuffer(this.dest)
        this.dest = null
      }
      this.indexFilter = null
    },
    // Централизованная обработка ошибок WASM
    handleWasmFailure() {
      cancelAnimationFrame(this.animationId)
      this.canSubscribe = false
      this.$emit('reload:ecg')
    },
    // Форматирование времени
    timeString(sec) {
      sec = Math.round(sec)
      return sec < 60 ? sec.toString() : `${Math.floor(sec / 60)}:${(sec % 60).toString().padStart(2, '0')}`
    },
    // Установка масштаба по оси Y
    setScaleY(containerId = 'canvas-conteiner') {
      let chartY = 30 * this.scaleY
      const container = document.getElementById(containerId)

      if (container) {
        if (chartY <= 500) {
          container.style.height = 'auto'
        } else {
          container.style.height = '500px'
        }
      }
    },
    // Подготовка сигнала
    prepareLocalSignal(signalFB) {
      const localSignal = []
      for (let leadNumber = 0; leadNumber < this.leadscount; leadNumber++) {
        localSignal[leadNumber] = []
        const id = +this.leadscount === +this.generalLeads ? leadNumber : +this.nChannel - 1

        for (let i = 0; i < signalFB.ecgdata(id).ecgdataLength(); i++) {
          localSignal[leadNumber].push(signalFB.ecgdata(id).ecgdata(i))
        }
      }
      return localSignal
    },
    changeData(signal, options = {}) {
      const signalFB = getSignal(signal.signal)
      const packageId = signalFB.id()

      localStorage.setItem(`lastDataTime${this.trainingId}`, signalFB.timestamp())
      this.nbOfSamples = signalFB.readingscount() / signalFB.leadscount()

      if (packageId < this.mappedId) {
        return
      }

      // Инициализация параметров
      if (!this.leadscount) {
        this.generalLeads = signalFB.leadscount()
        this.leadscount = options.fixedLeadscount ? 1 : signalFB.leadscount() > 2 ? 1 : signalFB.leadscount()

        if (!this.source) {
          try {
            const arraySize = options.fixedArraySize
              ? this.leadscount * 500
              : this.leadscount * this.nbOfSamples

            this.initializeBuffers(arraySize)
          } catch (e) {
            if (options.logErrors) console.log(e)
            cancelAnimationFrame(this.animationId)
            this.canSubscribe = false
            this.$emit('reload:ecg')
            return
          }
        }

        this.coefX = (250 / this.nbOfSamples) * RENDER_CONSTANTS.RENDER_SPEED_MULTIPLIER
      }

      this.countPoint += this.nbOfSamples
      const localSignal = this.prepareLocalSignal(signalFB)

      // Объединение сигналов в один массив
      const ecgSignalsJointInOneArray = []
      for (let leadNumber = 0; leadNumber < this.leadscount; leadNumber++) {
        ecgSignalsJointInOneArray.push(...localSignal[leadNumber])
      }

      // Создание объекта сигнала с ID
      const objJointSignalsEcgWithId = {
        id: packageId,
        signals: ecgSignalsJointInOneArray
      }

      // Вставка в буфер с сохранением порядка
      let minBeforeCurrentIndex = 0
      const c = this.localBufferNotFilter.length === 0 ? 0 : this.localBufferNotFilter.length

      for (let i = 0; i < c; i++) {
        if (this.localBufferNotFilter[i].id < packageId) {
          minBeforeCurrentIndex = i + 1
        } else {
          break
        }
      }

      this.localBufferNotFilter.splice(minBeforeCurrentIndex, 0, objJointSignalsEcgWithId)

      // Фильтрация при накоплении достаточного количества пакетов
      if (this.localBufferNotFilter.length > 3) {
        this.filteredSignalAndAddToLocalBuffer()
      }

      // Обработка последовательных пакетов
      if (this.localBufferNotFilter && this.localBuffer[0]) {
        while (
          this.localBufferNotFilter.length > 0 &&
          this.localBufferNotFilter[0].id - this.localBuffer[0][this.localBuffer[0].length - 1].id === 1
        ) {
          this.filteredSignalAndAddToLocalBuffer()
        }
      }
    },
    // Проверка на пропуск пакетов
    checkPackageGap() {
      if (!this.localBuffer[0] || !this.localBufferNotFilter[0]) return false
      const lastFilteredId = this.localBuffer[0][this.localBuffer[0].length - 1].id
      return this.localBufferNotFilter[0].id - lastFilteredId > 1
    },
    splitFilteredSignal(filterJointArr) {
      const filterArrs = []
      let useBitrate = 0
      for (let leadNumber = 0; leadNumber < this.leadscount; leadNumber++) {
        filterArrs[leadNumber] = filterJointArr.slice(useBitrate, useBitrate + this.nbOfSamples)
        useBitrate += this.nbOfSamples
      }
      return filterArrs
    },
    filteredSignalAndAddToLocalBuffer() {
      // Проверка обрывов
      if (this.checkPackageGap()) {
        if (!this.manageWasmBuffers('init')) return
      }

      let filterJointArr
      try {
        filterJointArr = this.doWasmFilter(this.localBufferNotFilter[0].signals)
      } catch (e) {
        console.error('WASM filtering error:', e)
        this.handleWasmFailure()
        return
      }

      let filterArrs = this.splitFilteredSignal(filterJointArr)
      let ecgSignal = []

      for (let leadNumber = 0; leadNumber < this.leadscount; leadNumber++) {
        this.setMinMax(leadNumber, filterArrs)

        ecgSignal[leadNumber] = []
        this.localBuffer[leadNumber] = this.localBuffer[leadNumber] ? this.localBuffer[leadNumber] : []
        for (let i = 0; i < filterArrs[leadNumber].length; i++) {
          ecgSignal[leadNumber].push({
            value: filterArrs[leadNumber][i]
          })
        }
        this.localBuffer[leadNumber].push({
          id: this.localBufferNotFilter[0].id,
          data: ecgSignal[leadNumber]
        })
      }
      this.localBufferNotFilter.shift()
    },
    initCanvasAndRender() {
      this.createCanvas()
      if (!this.animationId) {
        this.animationId = requestAnimationFrame(this.drawWave)
      }
    },
    handleFirstRender(leadNumber, newMin, newMax) {
      if (
        ((newMin !== 0 && this.minY[leadNumber] === 0) || (newMax !== 0 && this.maxY[leadNumber] === 0)) &&
        !this.forceRender
      ) {
        this.forceRender = true
        this.initCanvasAndRender()
      }
    },
    setMinMax(leadNumber, filterArrs) {
      const newMin = Math.min(...filterArrs[leadNumber])
      const newMax = Math.max(...filterArrs[leadNumber])

      // Инициализация при первом вызове
      if (this.minY[leadNumber] === undefined || this.maxY[leadNumber] === undefined) {
        this.minY[leadNumber] = newMin
        this.maxY[leadNumber] = newMax
      }

      // Определение порога значительного изменения (20% от текущего диапазона)
      const currentRange = this.maxY[leadNumber] - this.minY[leadNumber]
      const threshold = currentRange * 0.2

      // Проверка выхода за пределы текущего диапазона
      if (
        newMin < this.minY[leadNumber] - threshold || // Проверка на уменьшение минимума
        newMax > this.maxY[leadNumber] + threshold || // Проверка на увеличение максимума
        newMin > this.minY[leadNumber] + threshold || // Проверка на увеличение минимума
        newMax < this.maxY[leadNumber] - threshold // Проверка на уменьшение максимума
      ) {
        this.needsResize = true
        this.storageMinY[leadNumber] = newMin
        this.storageMaxY[leadNumber] = newMax
      }

      // Обработка первого рендера
      this.handleFirstRender(leadNumber, newMin, newMax)

      if (!this.forceRender && this.localBuffer[leadNumber] && this.localBuffer[leadNumber].length > 5) {
        this.forceRender = true
        this.initCanvasAndRender()
      }
    },
    handleCanvasResize() {
      if (this.needsResize && this.time === 0) {
        this.minY = [...this.storageMinY]
        this.maxY = [...this.storageMaxY]
        this.createCanvas()
      }
    },
    calcAndDrawCoordinate() {
      this.drawLoading = true
      let renderingSpeed = this.calculateRenderingSpeed()
      this.mappedId = this.localBuffer[0][0].id

      let x0, y0
      const coef = this.coefX
      while (renderingSpeed !== 0) {
        this.handleCanvasResize()

        let offsetSignal = RENDER_CONSTANTS.BASE_OFFSET

        x0 = this.time * coef * this.scaleX
        this.time = +(this.time + RENDER_CONSTANTS.TIME_STEP).toFixed(4)

        for (let leadNumber = 0; leadNumber < this.leadscount; leadNumber++) {
          const max = this.maxY[leadNumber]
          const min = this.minY[leadNumber]

          if (
            this.coordinateGraph &&
            this.leadscount > 2 &&
            this.coordinateGraph.length !== this.leadscount
          ) {
            this.coordinateGraph.push(offsetSignal)
          }

          if (this.localBuffer[leadNumber][0].data.length === 0) {
            this.localBuffer[leadNumber].shift()
          }

          y0 = this.getCoordinate(this.localBuffer[leadNumber][0].data[0].value, max, min, offsetSignal)

          this.localBuffer[leadNumber][0].data.shift()
          this.drawPoint(x0, y0, leadNumber)

          offsetSignal =
            this.getCoordinate(min, max, min, offsetSignal) + this.scaleY * RENDER_CONSTANTS.LEAD_OFFSET
        }

        const widthThreshold = this.$el.offsetWidth / (this.scaleX * this.coefX)
        if (this.time > widthThreshold) {
          this.time = 0
        }

        renderingSpeed = renderingSpeed - 1
      }
      this.drawLoading = false
    },
    calculateRenderingSpeed() {
      const baseSpeed = this.localBuffer[0].length > RENDER_CONSTANTS.MIN_BUFFER_PACKETS ? 6 : 3
      return Math.ceil(baseSpeed * (RENDER_CONSTANTS.RENDER_SPEED_MULTIPLIER / this.coefX))
    },
    drawWave() {
      this.animationId = requestAnimationFrame(this.drawWave)
      let self = this
      if (self.localBuffer[0] && self.localBuffer[0].length > 2 && this.drawLoading === false) {
        self.calcAndDrawCoordinate()
      }
    },

    doWasmFilter(data) {
      if (!data || !this.source || !this.dest) {
        console.error('Invalid data or buffers')
        return
      }
      try {
        let inArray = new Float32Array(data)
        this.copyToArray(this.source, inArray)
        this.doFilter(this.indexFilter, this.source, this.dest, data.length / this.leadscount)
        let copiedArray = this.ptrToArray(this.dest, data.length)
        return copiedArray
      } catch (error) {
        console.error('WASM filter error:', error)
        return []
      }
    },
    getCoordinate(sig, max, min, offset) {
      if (max - min == 0) return 1 + offset
      let chartY = (max - min) * 4000 * this.scaleY
      return chartY - (chartY / (max - min)) * (sig - min) + offset
    },
    drawPoint(x0, y0, leadNumber) {
      let x, y
      x = this.lastData[leadNumber] ? this.lastData[leadNumber].x : 0
      y = this.lastData[leadNumber] ? this.lastData[leadNumber].y : 0
      Object.assign(this.lastData, { [leadNumber]: { x: x0, y: y0 } })
      this.ctxEcg.beginPath()
      if (x0 !== 0) {
        this.ctxEcg.moveTo(x, y)
      }
      this.ctxEcg.lineTo(x0, y0)

      this.ctxEcg.stroke()
      this.ctxEcg.clearRect(x0 + 1, 0, 20, this.canvasEcg.height)

      if (this.ctxTime) {
        this.ctxTime.clearRect(x0 + 1, 0, 20, this.canvasEcg.height)
      }
    }
  }
}
